import { useCallback, useEffect, useState } from "react";
import { Dices, Loader2, RefreshCw, Square, Clock, CheckCircle2, XCircle, Ban } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const REFRESH_MS = 5000;
const fmtT = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
const STATUS = {
  done: { label: "Afgerond", Icon: CheckCircle2, cls: "text-positive" },
  failed: { label: "Mislukt", Icon: XCircle, cls: "text-destructive" },
  cancelled: { label: "Gestopt", Icon: Ban, cls: "text-muted-foreground" },
};
const th = "text-left px-3 py-2 font-display uppercase text-[10px] tracking-widest";

export default function McQueueBlock() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stopping, setStopping] = useState("");

  const load = useCallback(async () => {
    try { setData((await api.get("/admin/mc-queue")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, REFRESH_MS); return () => clearInterval(t); }, [load]);

  const stop = async (j) => {
    if (!window.confirm(`Simulatie van ${j.user_email} voor "${j.scenario_name}" stoppen?`)) return;
    setStopping(j.id);
    try { await api.delete(`/monte-carlo/jobs/${j.id}`); toast.success("Simulatie gestopt."); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setStopping("");
  };
  const refresh = async () => { setBusy(true); await load(); setBusy(false); };

  return (
    <section className="bg-card border-2 border-foreground heavy-shadow" data-testid="mc-queue-block">
      <div className="p-5 border-b-2 border-foreground flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight inline-flex items-center gap-2"><Dices className="w-5 h-5" /> Monte Carlo-wachtrij</h2>
          <p className="text-xs text-muted-foreground mt-1">Eén simulatie tegelijk, in volgorde van aanvraag. Ververst elke 5 seconden.</p>
        </div>
        <div className="flex items-center gap-2">
          {data && <span className="text-xs font-mono-num border-2 border-foreground px-2 py-1" data-testid="mc-queue-counts">{data.queue.running} lopend · {data.queue.queued} wachtend</span>}
          <button type="button" onClick={refresh} disabled={busy} className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="mc-queue-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Ververs
          </button>
        </div>
      </div>

      {!data ? <div className="p-5 text-sm text-muted-foreground">Laden…</div> : (
        <div className="divide-y-2 divide-foreground">
          <div className="overflow-x-auto">
            <p className="px-5 pt-4 pb-2 font-display uppercase font-bold text-xs tracking-widest">Lopend en wachtend</p>
            {data.active.length === 0 ? <div className="px-5 pb-4 text-sm text-muted-foreground" data-testid="mc-queue-active-empty">Geen simulaties in de wachtrij.</div> : (
              <table className="w-full text-sm" data-testid="mc-queue-active-table">
                <thead className="bg-foreground text-background"><tr>{["Status", "Gebruiker", "Club · scenario", "Runs", "Voortgang", "Aangevraagd / gestart", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.active.map((j, i) => (
                    <tr key={j.id} className="border-t border-border align-top" data-testid={`mc-queue-active-row-${i}`}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {j.status === "running"
                          ? <span className="inline-flex items-center gap-1 font-bold text-primary"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lopend</span>
                          : <span className="inline-flex items-center gap-1 font-bold text-secondary"><Clock className="w-3.5 h-3.5" /> Wachtend · positie {j.queue_position}</span>}
                      </td>
                      <td className="px-3 py-2 text-xs"><div className="font-bold">{j.user_email}</div><div className="text-muted-foreground">{j.user_name}</div></td>
                      <td className="px-3 py-2 text-xs">
                        <div className="text-muted-foreground">{j.club_name}</div>
                        {j.club_id ? <Link to={`/clubs/${j.club_id}/scenarios/${j.scenario_id}`} className="font-bold hover:underline">{j.scenario_name}</Link> : <span className="font-bold">{j.scenario_name}</span>}
                      </td>
                      <td className="px-3 py-2 font-mono-num text-xs">{Number(j.n_runs).toLocaleString("nl-NL")}</td>
                      <td className="px-3 py-2 min-w-[140px]">
                        <div className="h-3 border-2 border-foreground bg-background relative overflow-hidden">
                          <div className={`h-full ${j.status === "running" ? "bg-primary" : "bg-secondary/40 animate-pulse w-full"}`} style={j.status === "running" ? { width: `${j.progress}%` } : undefined} />
                        </div>
                        <div className="text-[10px] font-mono-num mt-0.5">{j.status === "running" ? `${j.progress}%` : "wacht"}</div>
                      </td>
                      <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{fmtT(j.created_at)}{j.started_at && <div className="text-muted-foreground">▶ {fmtT(j.started_at)}</div>}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => stop(j)} disabled={stopping === j.id} data-testid={`mc-queue-stop-${i}`}
                          className="btn-brutal bg-destructive text-white px-2.5 py-1 font-display uppercase font-bold text-[11px] inline-flex items-center gap-1 disabled:opacity-50">
                          {stopping === j.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />} Stop
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="overflow-x-auto">
            <p className="px-5 pt-4 pb-2 font-display uppercase font-bold text-xs tracking-widest">Laatste 10 afgeronde runs</p>
            {data.recent.length === 0 ? <div className="px-5 pb-4 text-sm text-muted-foreground" data-testid="mc-queue-recent-empty">Nog geen afgeronde simulaties.</div> : (
              <table className="w-full text-sm" data-testid="mc-queue-recent-table">
                <thead className="bg-foreground text-background"><tr>{["Status", "Gebruiker", "Club · scenario", "Runs", "Rekentijd", "Afgerond"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.recent.map((j, i) => {
                    const s = STATUS[j.status] || STATUS.done;
                    return (
                      <tr key={j.id} className="border-t border-border" data-testid={`mc-queue-recent-row-${i}`}>
                        <td className={`px-3 py-2 whitespace-nowrap font-bold inline-flex items-center gap-1 ${s.cls}`}><s.Icon className="w-3.5 h-3.5" /> {s.label}{j.error ? <span className="font-normal text-xs"> · {j.error}</span> : null}</td>
                        <td className="px-3 py-2 text-xs font-bold">{j.user_email}</td>
                        <td className="px-3 py-2 text-xs"><span className="text-muted-foreground">{j.club_name} · </span>{j.scenario_name}</td>
                        <td className="px-3 py-2 font-mono-num text-xs">{Number(j.n_runs).toLocaleString("nl-NL")}</td>
                        <td className="px-3 py-2 font-mono-num text-xs">{j.duration_s != null ? `${Math.round(j.duration_s)} s` : "—"}</td>
                        <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{fmtT(j.finished_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
